import i18n from 'i18next';
import { actions, assign, createMachine, sendParent } from 'xstate';
import { NavigationSections } from '../constants';
import { QueryParamKeys } from '../constants/query-params';
import {
  AppSteps,
  BankConnectionErrTypes,
  ChannelTypes,
  FormTypes,
  ItemValidationTypes,
  ProtectionPlusStatus,
  SoftDeclineTypes,
  UpdateBankAccountErrors,
  UpdateDebitErrors,
} from '../gql/graphql';
import { getCartFromOrderDetails } from '../helpers/converters/getCartFromOrderDetails';
import { isPostDecisionStatus } from '../helpers/converters/isPostDecisionStatus';
import { getEcommApprovalOnly } from '../helpers/leases/leases.helpers';
import { redirectToApplicationDetails, setAppStatusParams } from '../helpers/url/url.helpers';
import enrollInAutopay from '../networkRequests/bankAccount/autopayEnrollment';
import { plaidUpdateBankAccount, updateBankAccount } from '../networkRequests/bankAccount/updateBankAccount';
import getApplicationItems from '../networkRequests/cart/getApplicationItems';
import updateOrderItems from '../networkRequests/cart/updateOrderItems';
import updateDebitInfo from '../networkRequests/debitCard/updateDebitCard';
import updateEmployment from '../networkRequests/employment/updateEmployment';
import getIpAddress from '../networkRequests/ip/getIpAddress';
import acknowledgeLeaseOptions from '../networkRequests/lease/acknowledgeLeaseOptions';
import generateContract from '../networkRequests/lease/generateContract';
import getExistingLeaseDocuments from '../networkRequests/lease/getExistingLeaseDocuments';
import getProtectionPlusEnrollmentUrl from '../networkRequests/lease/getProtectionPlusEnrollmentUrl';
import selectLeaseOption from '../networkRequests/lease/selectLeaseOption';
import signLeaseAgreement from '../networkRequests/lease/signLeaseAgreement';
import getLoanDocs from '../networkRequests/loan/getLoanDocs';
import selectLoanOptionType from '../networkRequests/loan/selectLoanOption';
import signLoanAgreement from '../networkRequests/loan/signLoanAgreement';
import queryOrder from '../networkRequests/order/queryOrder';
import fetchLinkToken from '../networkRequests/plaid/getLinkToken';
import queryEmbedUrl, { GetEmbedUrlResult } from '../networkRequests/virtualCard/getEmbedURL';
import removeVirtualCard from '../networkRequests/virtualCard/removeVirtualCard';
import { PaymentSelectionErrors } from '../pages/paymentSelectionError/PaymentSelectionError';
import { merchantRoles } from '../types/role';
import getApplicationId from '../utils/commonState/getApplicationId';
import { PropagateNetworkErrors } from './actions/genericActions';
import {
  AssignExistingLeaseDocuments,
  AssignGeneratedLeaseDocuments,
  AssignInitialLeaseDocumentLanguage,
  createLanguageChangedTransitions,
  AssignRequestedLeaseDocumentLanguage,
  createLeaseDocumentLanguageReconciliation,
} from './actions/leaseDocumentLanguage';
import { identify, page, track } from './actions/segment';
import { trackContractSummaryAcknowledgeFailed } from './actions/trackContractSummaryAcknowledgeFailed';
import { MakeStateCallingQueryOrder, MakeStateCallingQueryOrderWithCartValidation } from './refresh-order';
import {
  ExistingApplicationEvents,
  ExistingApplicationMachineContext,
  ExistingApplicationMachineEvents,
  ExistingApplicationMachineGuards,
  ExistingApplicationMachineStates,
  ExistingApplicationStates,
} from './types/existingApplicationMachine.types';
import { determinePlaidFF } from '../helpers/plaid/plaid.helper';
import { EcommMachineEvents } from './types/ecommMachine.types';
import { PostDecisionStates } from './types/postDecisionMachine.types';
import { FLAG_SHOW_DEBIT_DECISION_SHORT_FORM } from '../constants/feature-flags';

const createSigningRefreshStates = () => ({
  refreshingSigningStatus: {
    tags: 'calling',
    invoke: {
      src: queryOrder,
      onDone: {
        target: 'checkSigningResult',
        actions: assign({
          application: (_: any, event: any) => event.data?.application,
          orderDetails: (_: any, event: any) => event.data?.details,
        }),
      },
      onError: {
        target: 'waiting',
        actions: PropagateNetworkErrors,
      },
    },
  },
  checkSigningResult: {
    always: [
      {
        cond: 'IsDocsSigned',
        target: 'routeAfterSigning',
      },
      {
        target: 'waiting',
      },
    ],
  },
});

// OR-3068 / OR-3067: the post-decision pages (approval, cart, contract summary, signing, the
// virtual card page) live in post-decision-machine. Every transition in this machine that used to
// target one of them now exits through ExistingApplicationStates.PostDecision instead, so this
// machine never renders them; the unreachable copies are deleted in OR-2999.
//
// The exit carries postDecisionStartingPage, which post-decision-machine honours as skipToPage.
// It is only set where the lease status alone would route somewhere else (its Entry lists the
// values it honours); left undefined, the submachine derives the page from context.
const exitToPostDecision = (postDecisionStartingPage?: string) => ({
  target: `#ExistingApplication.${ExistingApplicationStates.PostDecision}`,
  actions: assign({ postDecisionStartingPage }),
});

// The debit card update link on the post-decision contract summary sets fromContractSummary; the
// return has to go back to that page rather than wherever the status derives to.
const returnToContractSummary = {
  cond: (ctx: ExistingApplicationMachineContext) => ctx.fromContractSummary,
  target: `#ExistingApplication.${ExistingApplicationStates.PostDecision}`,
  actions: assign({
    postDecisionStartingPage: (ctx: ExistingApplicationMachineContext) =>
      ctx.dealer?.channelType === ChannelTypes.Ecommerce ? PostDecisionStates.ContractSummaryEcomm : PostDecisionStates.ContractSummaryBM,
  }),
};

export default createMachine<ExistingApplicationMachineContext, ExistingApplicationMachineEvents, ExistingApplicationMachineStates>(
  {
    id: 'ExistingApplication',
    initial: ExistingApplicationStates.ApplicationExists,
    context: {} as ExistingApplicationMachineContext, // Nothing you put here matters, the init block is the data in the `src: ExistingApplicationMachine` line in ecomm-machine
    on: {
      [ExistingApplicationEvents.TRACK]: {
        actions: (context, event: any) => track(event?.data?.eventName, event?.data?.payload)(context, event),
      },
      [ExistingApplicationEvents.PAGE]: {
        actions: (context, event: any) => page(event?.data?.pageName, event?.data?.payload)(context, event),
      },
      [ExistingApplicationEvents.IDENTIFY]: {
        actions: (context, event: any) => identify(event?.data?.customerSorId, event?.data?.payload),
      },
    },
    states: {
      [ExistingApplicationStates.ApplicationExists]: {
        always: [
          { target: ExistingApplicationStates.VirtualCardApplication, cond: 'VirtualCardDealer' },
          { target: ExistingApplicationStates.LeaseApplication },
        ],
      },
      [ExistingApplicationStates.LeaseApplication]: {
        initial: 'Select',
        states: {
          Select: {
            always: [
              // The debit card update from the post-decision contract summary arrives at approved
              // with this skip, so it sits ahead of the exit below.
              // The header back is driven by context here (DebitCardEntry has no canGoBackViaHeader
              // meta). Only the contract summary link gets it; the DebitCardError NEXT handoff
              // shares this skip and keeps the page without one, as every other entry does.
              {
                target: ExistingApplicationStates.DebitCardEntry,
                cond: 'LeaseSkipToDebitCardEntry',
                actions: assign({ canGoBackViaHeader: (ctx) => !!ctx.fromContractSummary }),
              },
              // OR-3068: approved, docsReady and the finished statuses are post-decision-machine's.
              // The ecomm machine sends every decided lease straight to PostDecision (its Loading
              // cascade and the customer lookup handoff), so only the debit card skip above can
              // arrive here with one. Ahead of the appStep resumes because AES leaves appStep on
              // the last step after the decision.
              { cond: (ctx) => isPostDecisionStatus(ctx.application), ...exitToPostDecision() },
              { target: ExistingApplicationStates.PaymentSelection, cond: 'EnterPaymentSelectFF' },
              { target: ExistingApplicationStates.BankDetails, cond: 'AppStepBankingInformation' }, // is this used?
              { target: ExistingApplicationStates.DebitCardEntry, cond: 'AppStepDebitInformation' },
              { target: ExistingApplicationStates.Prequalification, cond: 'LeaseSkipToPrequalification' }, // this is an entrypoint path and should be dedupped with preApproval when prequal and preapproval are synced
              { target: ExistingApplicationStates.BankAccountError, cond: 'LeaseBankAccountError' },
              { target: ExistingApplicationStates.DebitCardError, cond: 'LeasePaymentError' },
              { target: ExistingApplicationStates.LeaseDecline, cond: 'LeaseDecline' },
              { target: ExistingApplicationStates.Prequalification, cond: 'LeasePrequalified' }, // this should be dedupped with LeaseSkipToPrequalification
              { target: ExistingApplicationStates.PayFrequency },
            ],
          },
          [ExistingApplicationStates.Signed]: {},
          [ExistingApplicationStates.Prequalification]: {
            // defensively, always make sure you have the most up to date order. This is for things like approval amount and cart validation
            initial: 'callingQueryOrder',
            states: {
              callingQueryOrder: MakeStateCallingQueryOrderWithCartValidation('waiting'),
              waiting: { on: { [ExistingApplicationEvents.NEXT]: { target: '#ExistingApplication.LeaseApplication.PayFrequency' } } },
            },
            meta: {
              navigationSection: NavigationSections.HIDE_PROGRESS_BAR,
            },
          },
          [ExistingApplicationStates.PayFrequency]: {
            initial: 'PayFrequencyInit',
            states: {
              [ExistingApplicationStates.PayFrequencyInit]: {
                on: {
                  [ExistingApplicationEvents.NEXT]: [
                    {
                      target: ExistingApplicationStates.UpdateEmployment,
                      actions: [actions.assign({ payFrequencyDetails: (_context, event) => event.data })],
                    },
                  ],
                },
              },
              [ExistingApplicationStates.UpdateEmployment]: {
                tags: 'calling',
                invoke: {
                  src: (context) => updateEmployment(context.application, context.payFrequencyDetails),
                  onDone: [
                    {
                      cond: 'HasUpdateEmploymentErrors',
                      target: 'PayFrequencyInit',
                      actions: [assign({ updateEmploymentErrors: (_, event) => event.data?.updateEmploymentErrors })],
                    },
                    { target: ExistingApplicationStates.RefreshAfterEmployment },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
              [ExistingApplicationStates.RefreshAfterEmployment]: {
                tags: 'calling',
                invoke: {
                  src: queryOrder,
                  onDone: {
                    target: ExistingApplicationStates.RouteAfterEmployment,
                    actions: assign({
                      application: (_, event) => event.data?.application,
                      orderDetails: (_, event) => event.data?.details,
                    }),
                  },
                  onError: { actions: PropagateNetworkErrors },
                },
              },
              [ExistingApplicationStates.RouteAfterEmployment]: {
                always: [
                  // Feature flag takes precedence over all other checks.
                  // If enabled, show new payment selection screen
                  { target: '#ExistingApplication.LeaseApplication.PaymentSelection', cond: 'PaymentSelectFF' },
                  {
                    target: '#ExistingApplication.LeaseApplication.BankDetails',
                    cond: 'PerformBav',
                    actions: [actions.assign({ canGoBackViaHeader: true })],
                  },
                  //  If OTB lease flow, either go to contract summary or debit entry
                  { cond: 'isLeaseOTB', ...exitToPostDecision(PostDecisionStates.ContractSummaryEcomm) },
                  { target: '#ExistingApplication.LeaseApplication.DebitCardEntry', cond: 'IsCardExpired' },
                  // Debit Card entry is the default flow
                  { target: '#ExistingApplication.LeaseApplication.DebitCardEntry' },
                ],
              },
            },
            meta: {
              navigationSection: NavigationSections.PAYMENT_DETAILS,
            },
          },
          [ExistingApplicationStates.BankAccountError]: {
            on: {
              [ExistingApplicationEvents.NEXT]: {
                target: ExistingApplicationStates.BankDetails,
              },
            },
            meta: {
              navigationSection: NavigationSections.PAYMENT_DETAILS,
            },
          },
          [ExistingApplicationStates.BankDetails]: {
            initial: 'waiting',
            states: {
              waiting: {
                on: {
                  [ExistingApplicationEvents.NEXT]: [
                    {
                      target: 'callingUpdateBankAccount',
                      actions: [assign({ bankDetails: (_, event) => event?.data })],
                    },
                  ],
                },
              },
              callingUpdateBankAccount: {
                tags: 'calling',
                invoke: {
                  src: (context, event) =>
                    // We know application will have id at this point in the flow
                    // We also know that bank account is only currently present for leases
                    updateBankAccount(context.application?.lease!.id!, context.bankDetails?.accountNumber, context.bankDetails?.routingNumber),
                  onDone: [
                    {
                      cond: (_, event) => !!event.data.softDeclineError,
                      target: 'waiting',
                      actions: [
                        assign({
                          canGoBackViaHeader: (context) => context.dealer.formType !== FormTypes.Short,
                          updateBankAccountErr: (_, event) => event.data.softDeclineError,
                        }),
                      ],
                    },
                    {
                      cond: (_, event) => !!event.data.hardDeclineError,
                      target: '#ExistingApplication.BankAccountHardDecline',
                    },
                    {
                      target: '#ExistingApplication.LeaseApplication.DebitCardEntry',
                      actions: [assign({ canGoBackViaHeader: (context) => context.dealer.formType !== FormTypes.Short })],
                    },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
            },
            on: {
              [ExistingApplicationEvents.BACK]: [
                {
                  target: '#ExistingApplication.LeaseApplication.PaymentSelection',
                  cond: 'PaymentSelectFF',
                },
                {
                  target: '#ExistingApplication.LeaseApplication.PayFrequency',
                },
              ],
              [ExistingApplicationEvents.ERROR]: {
                target: '#ExistingApplication.LeaseApplication.PaymentSelectionError',
                actions: [assign({ bankConnection: (context) => ({ ...context.bankConnection, paymentSelectionError: PaymentSelectionErrors.plaidError }) })],
              },
            },
            meta: {
              navigationSection: NavigationSections.PAYMENT_DETAILS,
              canGoBackViaHeader: true,
            },
          },
          [ExistingApplicationStates.PaymentSelection]: {
            initial: 'PaymentSelectionInit',
            states: {
              PaymentSelectionInit: {
                on: {
                  [ExistingApplicationEvents.SELECT_PLAID]: { target: 'callingFetchLinkToken' },
                  [ExistingApplicationEvents.SELECT_MANUAL]: { target: '#ExistingApplication.LeaseApplication.BankDetails' },
                  [ExistingApplicationEvents.BACK]: { target: '#ExistingApplication.LeaseApplication.PayFrequency' },
                },
              },
              callingFetchLinkToken: {
                invoke: {
                  src: (context) => fetchLinkToken(context.orderId),
                  onDone: [
                    {
                      cond: 'LinkTokenFailure',
                      target: '#ExistingApplication.LeaseApplication.PaymentSelectionError',
                      actions: assign({
                        bankConnection: (context) => ({ ...context.bankConnection, paymentSelectionError: PaymentSelectionErrors.plaidError }),
                      }),
                    },
                    {
                      target: '#ExistingApplication.LeaseApplication.PlaidModal',
                      actions: assign({
                        plaidToken: (_context, event: any) => {
                          return {
                            linkToken: event.data.linkToken,
                            publicToken: '',
                          };
                        },
                      }),
                    },
                  ],
                  onError: {
                    target: '#ExistingApplication.LeaseApplication.PaymentSelectionError',
                    actions: assign({
                      bankConnection: (context) => ({
                        ...context.bankConnection,
                        paymentSelectionError: PaymentSelectionErrors.plaidError,
                      }),
                    }),
                  },
                },
              },
            },
            meta: {
              navigationSection: NavigationSections.PAYMENT_DETAILS,
              canGoBackViaHeader: true,
            },
          },
          [ExistingApplicationStates.PlaidModal]: {
            initial: 'PlaidModalInit',
            states: {
              PlaidModalInit: {
                on: {
                  [ExistingApplicationEvents.BACK]: {
                    target: '#ExistingApplication.LeaseApplication.PaymentSelection',
                  },
                  [ExistingApplicationEvents.ERROR]: {
                    target: '#ExistingApplication.LeaseApplication.PaymentSelectionError',
                    actions: [
                      actions.assign({
                        bankConnection: (context, event) => ({ ...context.bankConnection, paymentSelectionError: PaymentSelectionErrors.plaidError }),
                      }),
                    ],
                  },
                  [ExistingApplicationEvents.NEXT]: [
                    {
                      target: 'PlaidUpdateBankAccount',
                      actions: [
                        actions.assign({
                          bankDetails: (context, event) => {
                            return { ...context.bankDetails, bankName: event?.data?.bankName };
                          },
                          bankConnection: (context, event) => {
                            return { ...context.bankConnection, bankAcctLast4: event?.data?.bankAcctLast4 };
                          },
                          plaidToken: (context, event) => {
                            return { ...context.plaidToken, publicToken: event?.data?.publicToken };
                          },
                        }),
                      ],
                    },
                  ],
                },
              },
              PlaidUpdateBankAccount: {
                invoke: {
                  id: 'updateBankAccount',
                  src: (context, event) =>
                    // We know application will have id at this point in the flow
                    // We also know that bank account is only currently present for leases
                    plaidUpdateBankAccount(context.application?.lease!.id!, context.plaidToken.publicToken, context.plaidToken.linkToken),
                  onDone: [
                    {
                      cond: 'TokenizedAcct',
                      target: '#ExistingApplication.LeaseApplication.BankDetails',
                      actions: assign({
                        bankConnection: (context, event) => {
                          return { ...context.bankConnection, isTokenizedAcct: true };
                        },
                      }),
                    },
                    {
                      cond: 'InternetBankError',
                      target: '#ExistingApplication.LeaseApplication.PaymentSelectionError',
                      actions: assign({
                        bankConnection: (context, event) => {
                          return { ...context.bankConnection, paymentSelectionError: PaymentSelectionErrors.internetBankError };
                        },
                      }),
                    },
                    {
                      cond: 'BankError',
                      target: '#ExistingApplication.LeaseApplication.PaymentSelectionError',
                      actions: assign({
                        bankConnection: (context, event) => {
                          return { ...context.bankConnection, paymentSelectionError: PaymentSelectionErrors.plaidError };
                        },
                      }),
                    },
                    {
                      cond: 'BankAccountHardDecline',
                      target: '#ExistingApplication.BankAccountHardDecline',
                    },
                    {
                      cond: 'IneligibleBank',
                      target: '#ExistingApplication.LeaseApplication.PaymentSelectionError',
                      actions: assign({
                        bankConnection: (context, event) => {
                          return { ...context.bankConnection, paymentSelectionError: PaymentSelectionErrors.ineligibleError };
                        },
                      }),
                    },
                    {
                      cond: 'PlaidSuccess',
                      target: '#ExistingApplication.LeaseApplication.PaymentReview',
                    },
                  ],

                  onError: {
                    target: '#ExistingApplication.LeaseApplication.PaymentSelectionError',
                    actions: assign({
                      bankConnection: (context) => ({
                        ...context.bankConnection,
                        paymentSelectionError: PaymentSelectionErrors.plaidError,
                      }),
                    }),
                  },
                },
              },
            },
          },

          [ExistingApplicationStates.PaymentSelectionError]: {
            on: {
              [ExistingApplicationEvents.SELECT_PLAID]: [{ target: ExistingApplicationStates.PaymentSelection }],
              [ExistingApplicationEvents.SELECT_MANUAL]: {
                target: ExistingApplicationStates.BankDetails,
              },
            },
          },
          [ExistingApplicationStates.PaymentReview]: {
            on: {
              [ExistingApplicationEvents.NEXT]: {
                target: ExistingApplicationStates.DebitCardEntry,
              },
            },
          },
          [ExistingApplicationStates.ContractSummaryEcomm]: {
            initial: 'callingQueryOrder',
            states: {
              // Just a precaution to ensure contract summaries exist
              callingQueryOrder: MakeStateCallingQueryOrder('waiting'),
              waiting: {
                on: {
                  [ExistingApplicationEvents.NEXT]: [
                    {
                      target: 'callingSelectLeaseOption',
                      actions: assign({ selectLeaseOptionErrors: [] }),
                    },
                  ],
                },
              },
              callingSelectLeaseOption: {
                tags: 'calling',
                invoke: {
                  src: selectLeaseOption,
                  onDone: [
                    {
                      cond: (_, event) => event.data.selectLeaseOptionErrors?.length,
                      target: '#ExistingApplication.InternalError',
                    },
                    { target: '#ExistingApplication.LeaseApplication.LeaseAgreement' },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
            },
            on: {
              [ExistingApplicationEvents.EDIT_DEBIT_INFO]: {
                target: ExistingApplicationStates.DebitCardEntry,
                actions: assign({ fromContractSummary: true }),
              },
            },
            meta: {
              navigationSection: NavigationSections.REVIEW_AND_PAY,
            },
          },
          [ExistingApplicationStates.ContractSummaryBM]: {
            initial: 'callingQueryOrder',
            entry: assign({ showNotAcknowledgedModal: false }),
            states: {
              // Just a precaution to ensure contract summaries exist
              callingQueryOrder: MakeStateCallingQueryOrder('callingGetAppItems'),
              callingGetAppItems: {
                tags: 'calling',
                invoke: {
                  src: getApplicationItems,
                  onDone: {
                    target: 'waiting',
                    actions: assign({
                      applicationItems: (_, event) => {
                        return event.data || [];
                      },
                    }),
                  },
                  onError: { actions: PropagateNetworkErrors },
                },
              },
              waiting: {
                on: {
                  [ExistingApplicationEvents.NEXT]: [
                    {
                      target: 'callingAcknowledgeLeaseOptions',
                      actions: assign({ selectLeaseOptionErrors: [], acknowledgeLeaseTermsErrors: [], customerSelectedLeaseTerm: (_, event) => event.data }),
                    },
                  ],
                  [ExistingApplicationEvents.REFRESH_CONTRACT_SUMMARY_STATUS]: {
                    target: 'refreshingOrderStatus',
                    actions: assign({ showNotAcknowledgedModal: false }),
                  },
                  [ExistingApplicationEvents.CLOSE_NOT_ACKNOWLEDGED_MODAL]: {
                    actions: assign({ showNotAcknowledgedModal: false }),
                  },
                },
              },
              callingAcknowledgeLeaseOptions: {
                tags: 'calling',
                invoke: {
                  src: acknowledgeLeaseOptions,
                  onDone: [
                    {
                      cond: (_, event) => event.data.selectLeaseOptionErrors?.length,
                      target: '#ExistingApplication.InternalError',
                    },
                    {
                      cond: (_, event) => event.data.acknowledgeLeaseTermsErrors?.length,
                      target: 'waiting',
                      actions: [
                        assign({
                          acknowledgeLeaseTermsErrors: (_context, event) => event.data.acknowledgeLeaseTermsErrors ?? [],
                        }),
                        trackContractSummaryAcknowledgeFailed,
                      ],
                    },
                    { target: 'callingSelectLeaseOption', actions: track('Contract Summary Acknowledge Successful') },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
              refreshingOrderStatus: {
                tags: 'calling',
                invoke: {
                  src: queryOrder,
                  onDone: {
                    target: 'checkRefreshResult',
                    actions: assign({
                      application: (_, event) => event.data?.application,
                      orderDetails: (_, event) => event.data?.details,
                    }),
                  },
                  onError: {
                    target: 'waiting',
                    actions: PropagateNetworkErrors,
                  },
                },
              },
              checkRefreshResult: {
                always: [
                  {
                    target: '#ExistingApplication.LeaseApplication.LeaseAgreement',
                    cond: (context) => context.application?.lease?.status === 'docsReady',
                  },
                  {
                    actions: (context) => redirectToApplicationDetails(context.application?.lease?.publicId),
                    cond: (context) => context.application?.lease?.status === 'docsSigned',
                  },
                  {
                    target: 'waiting',
                    actions: assign({ showNotAcknowledgedModal: true }),
                  },
                ],
              },
              callingSelectLeaseOption: {
                tags: 'calling',
                invoke: {
                  src: selectLeaseOption,
                  onDone: [
                    {
                      cond: (_, event) => event.data.selectLeaseOptionErrors?.length,
                      target: '#ExistingApplication.InternalError',
                    },
                    { target: '#ExistingApplication.LeaseApplication.LeaseAgreement' },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
            },
            on: {
              [ExistingApplicationEvents.EDIT_DEBIT_INFO]: {
                target: ExistingApplicationStates.DebitCardEntry,
                actions: assign({ fromContractSummary: true }),
              },
            },
            meta: {
              navigationSection: NavigationSections.REVIEW_AND_PAY,
            },
          },
          [ExistingApplicationStates.LeaseAgreement]: {
            initial: 'callingGenerateContract',
            entry: AssignInitialLeaseDocumentLanguage,
            on: {
              [ExistingApplicationEvents.LANGUAGE_CHANGED]: {
                cond: (_context, event) => !!event.data?.language,
                actions: AssignRequestedLeaseDocumentLanguage,
              },
            },
            states: {
              callingGenerateContract: {
                tags: 'callingGenerateContract',
                invoke: {
                  src: (context, _) => generateContract(context.orderId),
                  onDone: [
                    {
                      cond: (_, event) => event.data?.generateContractErrors?.length,
                      target: 'waiting', // let the page handle it
                      actions: [
                        assign({ generateContractErrors: (_, event: any) => event.data?.generateContractErrors }),
                        track('Internal Error Occurred - Lease Agreement'),
                      ],
                    },
                    {
                      actions: AssignGeneratedLeaseDocuments,
                      target: 'waiting',
                    },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
                on: {
                  [ExistingApplicationEvents.LANGUAGE_CHANGED]: createLanguageChangedTransitions(),
                },
              },
              waiting: {
                always: createLeaseDocumentLanguageReconciliation(),
                on: {
                  [ExistingApplicationEvents.NEXT]: 'callingGetIp',
                  [ExistingApplicationEvents.REFRESH_SIGNING_STATUS]: 'refreshingSigningStatus',
                  [ExistingApplicationEvents.LANGUAGE_CHANGED]: createLanguageChangedTransitions(),
                },
              },
              callingGetIp: {
                tags: 'calling',
                invoke: {
                  src: getIpAddress,
                  onDone: [{ target: 'callingSignLeaseAgreement', actions: assign({ ipAddress: (_, event) => event.data.ipAddress }) }],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
              callingSignLeaseAgreement: {
                tags: 'calling',
                invoke: {
                  src: (context) => signLeaseAgreement(context.application?.lease?.id, context.ipAddress),
                  onDone: [
                    {
                      cond: (_, event) => event.data?.signingErrors?.length,
                      target: 'waiting',
                      actions: [assign({ signingErrors: (_, event: any) => event.data?.signingErrors }), track('Signing Error')],
                    },
                    {
                      target: 'routeAfterSigning',
                      // OR-2774: tracked here rather than on the LeaseSigned page, because
                      // merchant and Protection Plus routes never render it (see routeAfterSigning).
                      actions: [assign({ leaseSigned: true }), track('Lease Agreement Sign Successful')],
                    },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
              ...createSigningRefreshStates(),
              routeAfterSigning: {
                always: [
                  {
                    cond: 'OfferProtectionPlus',
                    target: 'routeToProtectionPlus',
                  },
                  {
                    cond: (context) => merchantRoles.includes(context.role),
                    target: 'redirectToAppDetails',
                  },
                  {
                    target: '#ExistingApplication.LeaseApplication.Signed',
                  },
                ],
              },
              redirectToAppDetails: {
                type: 'final',
                entry: (context) => redirectToApplicationDetails(context.application?.lease?.publicId),
              },
              // this state is only reachable via the contractSummaryPage transition
              callGetExistingLeaseDocuments: {
                tags: 'calling',
                invoke: {
                  src: (context, _) => getExistingLeaseDocuments(context.orderId),
                  onDone: [
                    {
                      cond: (_, event) => event.data?.getExistingLeaseDocumentsErrors?.length,
                      target: 'waiting', // let the page handle it
                      actions: [
                        assign({ getExistingLeaseDocumentsErrors: (_, event: any) => event.data?.getExistingLeaseDocumentsErrors }),
                        track('Internal Error Occurred - Getting Lease Agreement'),
                      ],
                    },
                    {
                      actions: AssignExistingLeaseDocuments,
                      target: 'waiting',
                    },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
              routeToProtectionPlus: {
                tags: 'calling',
                invoke: {
                  src: (context) => getProtectionPlusEnrollmentUrl(context.application?.lease?.id!),
                  onDone: [
                    {
                      cond: (_, event) => event.data?.errors?.length > 0,
                      target: '#ExistingApplication.LeaseApplication.Signed', //should there be an error screen saying error occured getting P+ enrollment url?
                      actions: assign({ protectionPlusEnrollmentErrors: (_, event) => event.data.errors }),
                    },
                    {
                      actions: (_, event) => {
                        const url = new URL(event.data.url);
                        url.searchParams.set(QueryParamKeys.Language, i18n.resolvedLanguage ?? 'en');
                        window.location.href = url.toString();
                      },
                    },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
            },
            meta: { navigationSection: NavigationSections.REVIEW_AND_PAY },
          },
          [ExistingApplicationStates.DebitCardError]: {
            on: {
              [ExistingApplicationEvents.NEXT]: {
                target: ExistingApplicationStates.DebitCardEntry,
              },
            },
            meta: {
              navigationSection: NavigationSections.PAYMENT_DETAILS,
            },
          },
          [ExistingApplicationStates.DebitCardEntry]: {
            initial: 'waiting',
            // cleared on the way out rather than on entering select, so the returnToContractSummary
            // guards still see it; a prepaid card retry stays inside and keeps it. The header back
            // goes with it so the contract summary does not inherit it through the handoff.
            exit: assign({ fromContractSummary: false, canGoBackViaHeader: false }),
            states: {
              waiting: {
                on: {
                  [ExistingApplicationEvents.NEXT]: {
                    target: 'callingUpdateDebitInfo',
                    actions: assign({
                      debitInfo: (_context, event) => event.data.debitDetails,
                      billingAddress: (_context, event) => event.data.billingAddress,
                    }),
                  },
                },
              },
              callingUpdateDebitInfo: {
                tags: 'calling',
                invoke: {
                  src: (context: ExistingApplicationMachineContext, event: ExistingApplicationMachineEvents) =>
                    updateDebitInfo(getApplicationId(context.application), context.debitInfo, context.billingAddress!),
                  onDone: [
                    {
                      target: 'callingQueryOrder',
                      actions: assign({ updateDebitInfoErrors: (_context, event) => event.data.updateDebitInfoErrors }),
                    },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
              callingQueryOrder: MakeStateCallingQueryOrder('select'),
              select: {
                always: [
                  {
                    target: '#ExistingApplication.DebitCardHardDecline',
                    cond: 'DebitCardHardDecline',
                  },
                  {
                    target: 'waiting',
                    cond: 'DebitCardPrepaidError',
                  },
                  {
                    target: '#ExistingApplication.LeaseApplication.DebitCardError',
                    cond: 'DebitCardSoftDeclineAndErrorDetailsEnabled',
                  },
                  {
                    target: '#ExistingApplication.DebitCardSoftDecline',
                    cond: 'DebitCardSoftDecline',
                  },
                  {
                    target: '#ExistingApplication.DebitCardFatalError',
                    cond: 'DebitCardFatalError',
                  },
                  //check errors first before evaluating success transition
                  // OR-3068: the initial payment is authorized and the rest is post-decision-machine's,
                  // which derives the page from the refreshed order. The one pin is the return from
                  // the debit card update link.
                  returnToContractSummary,
                  exitToPostDecision(),
                ],
              },
            },
            on: {
              [ExistingApplicationEvents.BACK]: [
                // the contract summary link came from post-decision-machine, so BACK returns there
                returnToContractSummary,
                { target: ExistingApplicationStates.BankDetails, cond: 'PerformBav' },
                { target: ExistingApplicationStates.PayFrequency },
              ],
            },
            meta: {
              navigationSection: NavigationSections.PAYMENT_DETAILS,
            },
          },
          [ExistingApplicationStates.LeaseDecline]: {
            type: 'final',
          },
        },
      },
      [ExistingApplicationStates.LoanApplication]: {
        initial: 'Select',
        states: {
          Select: {
            always: [{ target: ExistingApplicationStates.LoanDecline, cond: 'LoanDecline' }, { target: ExistingApplicationStates.PaymentSchedule }],
          },
          [ExistingApplicationStates.PaymentSchedule]: {
            initial: 'checkingForLoanSummary',
            states: {
              checkingForLoanSummary: {
                always: [
                  { cond: (context, _) => !!context.application?.loan?.summary, target: 'callingGetLoanDocs' },
                  { target: 'callingSelectLoanOptionType' },
                ],
              },
              callingSelectLoanOptionType: {
                tags: 'calling',
                invoke: {
                  src: selectLoanOptionType,
                  onDone: [
                    {
                      cond: (_, event) => event.data?.selectLoanOptionError?.length,
                      target: '#ExistingApplication.InternalError',
                    },
                    {
                      actions: assign({
                        selectLoanOptionResponse: (_, event: any) => event.data?.selectLoanOptionResponse,

                        // This assignment replaces the setSummary action
                        application: (context, event: any) => {
                          const currentApplication = context.application;
                          currentApplication!.loan!.summary = event.data?.selectLoanOptionResponse;
                          return currentApplication;
                        },
                      }),
                      target: 'callingGetLoanDocs',
                    },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
              callingGetLoanDocs: {
                tags: 'calling',
                invoke: {
                  src: (context, _) => getLoanDocs(context.orderId),
                  onDone: [
                    {
                      cond: (_, event) => event.data?.loanDocsError?.length,
                      target: '#ExistingApplication.InternalError',
                    },
                    {
                      actions: assign({
                        loanDocs: (_, event: any) => event.data?.loanDocs,
                      }),
                      target: 'waiting',
                    },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
              callingSignAgreement: {
                tags: 'calling',
                invoke: {
                  src: (_, event) => signLoanAgreement(event.data),
                  onDone: [
                    {
                      cond: (_, event) => event.data?.signLoanAgreementErrors?.length,
                      target: '#ExistingApplication.InternalError',
                    },
                    {
                      actions: assign({
                        signingErrors: (_, event: any) => event.data?.signLoanAgreementErrors,
                        loanSigned: (_, event: any) => event.data?.loanAgreementSigned,
                      }),
                      target: '#ExistingApplication.LoanApplication.AutoPayEnrollment',
                    },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
              waiting: {
                on: {
                  [ExistingApplicationEvents.SIGN_AGREEMENT]: {
                    target: 'callingSignAgreement',
                  },
                },
              },
            },
            meta: {
              navigationSection: NavigationSections.REVIEW_AND_PAY,
            },
          },
          // This state is only reached in Loan apps, which is to say it is never reached.
          [ExistingApplicationStates.AutoPayEnrollment]: {
            initial: 'waiting',
            states: {
              waiting: {
                on: {
                  [ExistingApplicationEvents.NEXT]: { target: 'callingEnrollInAutopay' },
                  [ExistingApplicationEvents.SKIP]: { target: '#ExistingApplication.FinalApproval' },
                },
              },
              callingEnrollInAutopay: {
                tags: 'calling',
                invoke: {
                  src: (context, event) => enrollInAutopay(context.orderId, event.data.accountDetails),
                  onDone: [
                    {
                      cond: (_, event) => event.data.autopayErrors?.length,
                      target: 'waiting',
                      actions: [
                        assign({ autopayErrors: (_, event) => event.data.autopayErrors }),
                        (context, event) => track('Autopay Enrollment Error', { error: event.data.autopayErrors })(context, event),
                      ],
                    },
                    { target: '#ExistingApplication.FinalApproval' },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
            },
            meta: {
              navigationSection: NavigationSections.REVIEW_AND_PAY,
            },
          },
          [ExistingApplicationStates.LoanDecline]: {
            type: 'final',
          },
        },
      },
      [ExistingApplicationStates.VirtualCardApplication]: {
        initial: 'callingQueryOrder',
        states: {
          callingQueryOrder: MakeStateCallingQueryOrderWithCartValidation(ExistingApplicationStates.Loading),
          [ExistingApplicationStates.Loading]: {
            always: [
              // OR-3067: approved, docsReady and the finished statuses exit to post-decision-machine,
              // whose RouteApproved, RouteDocsReady and RouteFinished replace the VirtualCardPage,
              // ApprovalWithSkipItemEntry, docsReady, contract summary access, ecomm cart and
              // CheckLeaseStatus approval branches that lived here. The CartSummary and LeaseAgreement
              // skips went with the entrypoint contract summary handoff, which sends POST_DECISION
              // now. The pre-decision skips stay ahead, the way they were.
              { target: ExistingApplicationStates.PaymentSelection, cond: 'EnterPaymentSelectFF' },
              { target: ExistingApplicationStates.BankDetails, cond: 'LeaseSkipToBankDetails' },
              // The header back is driven by context here (DebitCardEntry has no canGoBackViaHeader
              // meta). Only the contract summary link gets it; the DebitCardError NEXT handoff
              // shares this skip and keeps the page without one, as every other entry does.
              {
                target: ExistingApplicationStates.DebitCardEntry,
                cond: 'LeaseSkipToDebitCardEntry',
                actions: assign({ canGoBackViaHeader: (ctx) => !!ctx.fromContractSummary }),
              },
              { cond: (ctx) => isPostDecisionStatus(ctx.application), ...exitToPostDecision() },
              { target: ExistingApplicationStates.CheckLeaseStatus },
            ],
            meta: {
              navigationSection: NavigationSections.HIDE_PROGRESS_BAR,
            },
          },
          [ExistingApplicationStates.Prequalification]: {
            // defensively, always make sure you have the most up to date order. This is for things like approval amount and cart validation
            initial: 'callingQueryOrder',
            states: {
              callingQueryOrder: MakeStateCallingQueryOrderWithCartValidation('waiting'),
              waiting: { on: { [ExistingApplicationEvents.NEXT]: { target: '#ExistingApplication.VirtualCardApplication.PayFrequency' } } },
            },
            meta: {
              navigationSection: NavigationSections.HIDE_PROGRESS_BAR,
            },
          },
          [ExistingApplicationStates.CheckLeaseStatus]: {
            always: [
              // an approved lease exits to post-decision-machine from the ecomm machine's Loading
              // cascade, so the approved branch that used to open this list is gone
              // The header back is driven by context here (DebitCardEntry has no canGoBackViaHeader
              // meta). Only the contract summary link gets it; the DebitCardError NEXT handoff
              // shares this skip and keeps the page without one, as every other entry does.
              {
                target: ExistingApplicationStates.DebitCardEntry,
                cond: 'LeaseSkipToDebitCardEntry',
                actions: assign({ canGoBackViaHeader: (ctx) => !!ctx.fromContractSummary }),
              },
              { target: ExistingApplicationStates.BankAccountError, cond: 'LeaseBankAccountError' },
              { target: ExistingApplicationStates.DebitCardError, cond: 'LeasePaymentError' },
              { target: ExistingApplicationStates.Prequalification, cond: 'LeasePrequalified' },
              { target: ExistingApplicationStates.PayFrequency },
            ],
          },

          [ExistingApplicationStates.PayFrequency]: {
            initial: 'PayFrequencyInit',
            states: {
              [ExistingApplicationStates.PayFrequencyInit]: {
                on: {
                  [ExistingApplicationEvents.NEXT]: [
                    {
                      target: ExistingApplicationStates.UpdateEmployment,
                      actions: [actions.assign({ payFrequencyDetails: (_context, event) => event.data })],
                    },
                  ],
                },
              },
              [ExistingApplicationStates.UpdateEmployment]: {
                tags: 'calling',
                invoke: {
                  src: (context) => updateEmployment(context.application, context.payFrequencyDetails),
                  onDone: [
                    {
                      cond: 'HasUpdateEmploymentErrors',
                      target: 'PayFrequencyInit',
                      actions: [assign({ updateEmploymentErrors: (_, event) => event.data?.updateEmploymentErrors })],
                    },
                    { target: ExistingApplicationStates.RefreshAfterEmployment },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
              [ExistingApplicationStates.RefreshAfterEmployment]: {
                tags: 'calling',
                invoke: {
                  src: queryOrder,
                  onDone: {
                    target: ExistingApplicationStates.RouteAfterEmployment,
                    actions: assign({
                      application: (_, event) => event.data?.application,
                      orderDetails: (_, event) => event.data?.details,
                    }),
                  },
                  onError: { actions: PropagateNetworkErrors },
                },
              },
              [ExistingApplicationStates.RouteAfterEmployment]: {
                always: [
                  { target: '#ExistingApplication.VirtualCardApplication.PaymentSelection', cond: 'PaymentSelectFF' },
                  {
                    cond: 'PerformBav',
                    target: '#ExistingApplication.VirtualCardApplication.BankDetails',
                    actions: [actions.assign({ canGoBackViaHeader: true })],
                  },
                  // Debit Card entry is the default flow
                  { target: '#ExistingApplication.VirtualCardApplication.DebitCardEntry' },
                ],
              },
            },
            meta: {
              navigationSection: NavigationSections.PAYMENT_DETAILS,
            },
          },

          [ExistingApplicationStates.BankAccountError]: {
            on: {
              [ExistingApplicationEvents.NEXT]: {
                target: ExistingApplicationStates.BankDetails,
              },
            },
            meta: {
              navigationSection: NavigationSections.PAYMENT_DETAILS,
            },
          },

          [ExistingApplicationStates.BankDetails]: {
            initial: 'waiting',
            states: {
              waiting: {
                on: {
                  [ExistingApplicationEvents.NEXT]: [
                    {
                      target: 'callingUpdateBankAccount',
                      actions: [assign({ bankDetails: (_, event) => event?.data })],
                    },
                  ],
                },
              },
              callingUpdateBankAccount: {
                tags: 'calling',
                invoke: {
                  src: (context, event) =>
                    // We know application will have id at this point in the flow
                    // We also know that bank account is only currently present for leases
                    updateBankAccount(context.application?.lease!.id!, context.bankDetails?.accountNumber, context.bankDetails?.routingNumber),
                  onDone: [
                    {
                      cond: (_, event) => !!event.data.softDeclineError,
                      target: 'waiting',
                      actions: [
                        assign({
                          canGoBackViaHeader: (context) => context.dealer.formType !== FormTypes.Short,
                          updateBankAccountErr: (_, event) => event.data.softDeclineError,
                        }),
                      ],
                    },
                    {
                      cond: (_, event) => !!event.data.hardDeclineError,
                      target: '#ExistingApplication.BankAccountHardDecline',
                    },
                    {
                      target: '#ExistingApplication.VirtualCardApplication.DebitCardEntry',
                      actions: [assign({ canGoBackViaHeader: (context) => context.dealer.formType !== FormTypes.Short })],
                    },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
            },
            on: {
              [ExistingApplicationEvents.BACK]: [{ target: '#ExistingApplication.VirtualCardApplication.PayFrequency' }],
            },
            meta: {
              navigationSection: NavigationSections.PAYMENT_DETAILS,
              canGoBackViaHeader: true,
            },
          },

          [ExistingApplicationStates.PaymentSelection]: {
            initial: 'PaymentSelectionInit',
            states: {
              PaymentSelectionInit: {
                on: {
                  [ExistingApplicationEvents.SELECT_PLAID]: { target: 'callingFetchLinkToken' },
                  [ExistingApplicationEvents.SELECT_MANUAL]: { target: '#ExistingApplication.VirtualCardApplication.BankDetails' },
                  [ExistingApplicationEvents.BACK]: { target: '#ExistingApplication.VirtualCardApplication.PayFrequency' },
                },
              },
              callingFetchLinkToken: {
                invoke: {
                  src: (context) => fetchLinkToken(context.orderId),
                  onDone: [
                    {
                      cond: 'LinkTokenFailure',
                      target: '#ExistingApplication.VirtualCardApplication.PaymentSelectionError',
                      actions: assign({
                        bankConnection: (context) => ({ ...context.bankConnection, paymentSelectionError: PaymentSelectionErrors.plaidError }),
                      }),
                    },
                    {
                      target: '#ExistingApplication.VirtualCardApplication.PlaidModal',
                      actions: assign({
                        plaidToken: (_context, event: any) => {
                          return {
                            linkToken: event.data.linkToken,
                            publicToken: '',
                          };
                        },
                      }),
                    },
                  ],
                  onError: {
                    target: '#ExistingApplication.VirtualCardApplication.PaymentSelectionError',
                    actions: assign({
                      bankConnection: (context) => ({
                        ...context.bankConnection,
                        paymentSelectionError: PaymentSelectionErrors.plaidError,
                      }),
                    }),
                  },
                },
              },
            },
            meta: {
              navigationSection: NavigationSections.PAYMENT_DETAILS,
              canGoBackViaHeader: true,
            },
          },
          [ExistingApplicationStates.PlaidModal]: {
            initial: 'PlaidModalInit',
            states: {
              PlaidModalInit: {
                on: {
                  [ExistingApplicationEvents.BACK]: {
                    target: '#ExistingApplication.VirtualCardApplication.PaymentSelection',
                  },
                  [ExistingApplicationEvents.ERROR]: {
                    target: '#ExistingApplication.VirtualCardApplication.PaymentSelectionError',
                    actions: [
                      actions.assign({
                        bankConnection: (context, event) => ({ ...context.bankConnection, paymentSelectionError: PaymentSelectionErrors.plaidError }),
                      }),
                    ],
                  },
                  [ExistingApplicationEvents.NEXT]: [
                    {
                      target: 'PlaidUpdateBankAccount',
                      actions: [
                        actions.assign({
                          bankDetails: (context, event) => {
                            return { ...context.bankDetails, bankName: event?.data?.bankName };
                          },
                          bankConnection: (context, event) => {
                            return { ...context.bankConnection, bankAcctLast4: event?.data?.bankAcctLast4 };
                          },
                          plaidToken: (context, event) => {
                            return { ...context.plaidToken, publicToken: event?.data?.publicToken };
                          },
                        }),
                      ],
                    },
                  ],
                },
              },
              PlaidUpdateBankAccount: {
                invoke: {
                  id: 'updateBankAccount',
                  src: (context, event) =>
                    plaidUpdateBankAccount(context.application?.lease!.id!, context.plaidToken.publicToken, context.plaidToken.linkToken),
                  onDone: [
                    {
                      cond: 'TokenizedAcct',
                      target: '#ExistingApplication.VirtualCardApplication.BankDetails',
                      actions: assign({
                        bankConnection: (context, event) => {
                          return { ...context.bankConnection, isTokenizedAcct: true };
                        },
                      }),
                    },
                    {
                      cond: 'InternetBankError',
                      target: '#ExistingApplication.VirtualCardApplication.PaymentSelectionError',
                      actions: assign({
                        bankConnection: (context, event) => {
                          return { ...context.bankConnection, paymentSelectionError: PaymentSelectionErrors.internetBankError };
                        },
                      }),
                    },
                    {
                      cond: 'BankError',
                      target: '#ExistingApplication.VirtualCardApplication.PaymentSelectionError',
                      actions: assign({
                        bankConnection: (context, event) => {
                          return { ...context.bankConnection, paymentSelectionError: PaymentSelectionErrors.plaidError };
                        },
                      }),
                    },
                    {
                      cond: 'BankAccountHardDecline',
                      target: '#ExistingApplication.BankAccountHardDecline',
                    },
                    {
                      cond: 'IneligibleBank',
                      target: '#ExistingApplication.VirtualCardApplication.PaymentSelectionError',
                      actions: assign({
                        bankConnection: (context, event) => {
                          return { ...context.bankConnection, paymentSelectionError: PaymentSelectionErrors.ineligibleError };
                        },
                      }),
                    },
                    {
                      cond: 'PlaidSuccess',
                      target: '#ExistingApplication.VirtualCardApplication.PaymentReview',
                    },
                  ],

                  onError: {
                    target: '#ExistingApplication.VirtualCardApplication.PaymentSelectionError',
                    actions: assign({
                      bankConnection: (context) => ({
                        ...context.bankConnection,
                        paymentSelectionError: PaymentSelectionErrors.plaidError,
                      }),
                    }),
                  },
                },
              },
            },
          },

          [ExistingApplicationStates.PaymentSelectionError]: {
            on: {
              [ExistingApplicationEvents.SELECT_PLAID]: [{ target: ExistingApplicationStates.PaymentSelection }],
              [ExistingApplicationEvents.SELECT_MANUAL]: {
                target: ExistingApplicationStates.BankDetails,
              },
            },
          },
          [ExistingApplicationStates.PaymentReview]: {
            on: {
              [ExistingApplicationEvents.NEXT]: {
                target: ExistingApplicationStates.DebitCardEntry,
              },
            },
          },

          [ExistingApplicationStates.DebitCardError]: {
            on: {
              [ExistingApplicationEvents.NEXT]: {
                target: ExistingApplicationStates.DebitCardEntry,
              },
            },
            meta: {
              navigationSection: NavigationSections.PAYMENT_DETAILS,
            },
          },
          [ExistingApplicationStates.DebitCardEntry]: {
            initial: 'waiting',
            // cleared on the way out rather than on entering select, so the returnToContractSummary
            // guards still see it; a prepaid card retry stays inside and keeps it. The header back
            // goes with it so the contract summary does not inherit it through the handoff.
            exit: assign({ fromContractSummary: false, canGoBackViaHeader: false }),
            states: {
              waiting: {
                on: {
                  [ExistingApplicationEvents.NEXT]: {
                    target: 'callingUpdateDebitInfo',
                    actions: assign({
                      debitInfo: (_context, event) => event.data.debitDetails,
                      billingAddress: (_context, event) => event.data.billingAddress,
                    }),
                  },
                },
              },
              callingUpdateDebitInfo: {
                tags: 'calling',
                invoke: {
                  src: (context: ExistingApplicationMachineContext, event: ExistingApplicationMachineEvents) =>
                    updateDebitInfo(getApplicationId(context.application), context.debitInfo, context.billingAddress!),
                  onDone: [
                    {
                      target: 'callingQueryOrder',
                      actions: assign({ updateDebitInfoErrors: (_context, event) => event.data.updateDebitInfoErrors }),
                    },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
              callingQueryOrder: MakeStateCallingQueryOrder('select'),
              select: {
                always: [
                  {
                    target: '#ExistingApplication.DebitCardHardDecline',
                    cond: 'DebitCardHardDecline',
                  },
                  {
                    target: 'waiting',
                    cond: 'DebitCardPrepaidError',
                  },
                  {
                    target: '#ExistingApplication.VirtualCardApplication.DebitCardError',
                    cond: 'DebitCardSoftDeclineAndErrorDetailsEnabled',
                  },
                  {
                    target: '#ExistingApplication.DebitCardSoftDecline',
                    cond: 'DebitCardSoftDecline',
                  },
                  {
                    target: '#ExistingApplication.DebitCardFatalError',
                    cond: 'DebitCardFatalError',
                  },
                  //check errors first before evaluating success transition
                  // debit card update guard
                  returnToContractSummary,
                  // Normally this would go to approval due to status checks but
                  // approval is pinned here because shop first flows may already have a valid cart already
                  // which would inadvertently skip the approval page.
                  exitToPostDecision(PostDecisionStates.Approval),
                ],
              },
            },
            on: {
              [ExistingApplicationEvents.BACK]: [
                // the contract summary link came from post-decision-machine, so BACK returns there
                returnToContractSummary,
                { target: ExistingApplicationStates.BankDetails, cond: 'PerformBav' },
                { target: ExistingApplicationStates.PayFrequency },
              ],
            },
            meta: {
              navigationSection: NavigationSections.PAYMENT_DETAILS,
            },
          },
          [ExistingApplicationStates.Approval]: {
            // defensively, always make sure you have the most up to date order. This is for things like approval amount and cart validation
            initial: 'callingQueryOrder',
            states: {
              callingQueryOrder: MakeStateCallingQueryOrderWithCartValidation('waiting'),
              waiting: {
                on: {
                  [ExistingApplicationEvents.NEXT]: [
                    {
                      // If the order has items, then the customer must have already confirmed their cart total so they should go to contract summary next
                      target: '#ExistingApplication.VirtualCardApplication.ContractSummaryEcomm',
                      cond: 'OrderHasItems',
                    },
                    {
                      target: '#ExistingApplication.VirtualCardApplication.ItemEntry',
                    },
                  ],
                },
              },
            },
            meta: {
              canGoBackViaHeader: false,
            },
          },
          [ExistingApplicationStates.ItemEntry]: {
            on: {
              [ExistingApplicationEvents.BACK]: {
                actions: [
                  actions.assign({
                    canGoBackViaHeader: false,
                    itemToEdit: null,
                  }),
                ],
                target: ExistingApplicationStates.CartSummary,
              },
              [ExistingApplicationEvents.NEXT]: [
                {
                  cond: (context: ExistingApplicationMachineContext) => context.itemToEdit !== null,
                  actions: [
                    actions.assign({
                      cart: (context: ExistingApplicationMachineContext, event) => context.cart.toSpliced(context.itemToEdit!.index, 1, event.data),
                      itemToEdit: null,
                      canGoBackViaHeader: false,
                    }),
                    track('Item Updated'),
                  ],
                  target: ExistingApplicationStates.CartSummary,
                },
                {
                  actions: [
                    actions.assign({
                      cart: (context: ExistingApplicationMachineContext, event) => [...context.cart, event.data],
                      canGoBackViaHeader: false,
                    }),
                    track('Item Added'),
                  ],
                  target: ExistingApplicationStates.CartSummary,
                },
              ],
            },
            meta: {
              navigationSection: NavigationSections.PAYMENT_DETAILS,
            },
          },
          [ExistingApplicationStates.CartSummary]: {
            // remove blocked items from errors list and from cart. This shouldnt do anything in phase 2 because aes will not return blocks
            entry: assign((context) => {
              if (context.cartValidationItemErrors) {
                track('Autoremoved items', {
                  items: Object.values(context.cartValidationItemErrors)
                    .map((v) => v.displayName)
                    .join(', '),
                });

                return {
                  autoremovedCartItemNames: Object.values(context.cartValidationItemErrors).map((v) => v.displayName!),
                  cartValidationItemErrors: {},
                  cart: context.cart.filter((item) => !context.cartValidationItemErrors?.[item.id]), // return items in the cart that dont have a matching cv item block
                };
              }
              return {}; // this makes no changes to context, it does not assign a blank context
            }),

            on: {
              [ExistingApplicationEvents.BACK]: [
                {
                  actions: [
                    actions.assign({
                      canGoBackViaHeader: true,
                    }),
                  ],
                  target: ExistingApplicationStates.ItemEntry,
                  cond: 'HasItems',
                },
                {
                  target: ExistingApplicationStates.ItemEntry,
                },
              ],
              [ExistingApplicationEvents.EDIT_CART_ITEM]: {
                target: ExistingApplicationStates.ItemEntry,
                actions: [
                  actions.assign({
                    itemToEdit: (_context, event) => event.data.item,
                    canGoBackViaHeader: true,
                  }),
                ],
              },
              [ExistingApplicationEvents.DELETE_CART_ITEM]: [
                // if deleting last item, transition to itemEntry after
                {
                  cond: (ctx, event) => ctx.cart.length === 1,
                  target: ExistingApplicationStates.ItemEntry,
                  actions: [actions.assign({ cart: (context, event) => context.cart.toSpliced(event.data.index, 1) })],
                },
                { actions: [actions.assign({ cart: (context, event) => context.cart.toSpliced(event.data.index, 1) })] },
              ],
              [ExistingApplicationEvents.NEXT]: {
                target: ExistingApplicationStates.CartTotal,
              },
            },
            meta: {
              navigationSection: NavigationSections.PAYMENT_DETAILS,
            },
          },
          [ExistingApplicationStates.CartTotal]: {
            on: {
              [ExistingApplicationEvents.NEXT]: {
                target: ExistingApplicationStates.CartReviewLoading,
                actions: assign({ cartTotal: (_, event) => event.data.cartTotal }),
              },
              [ExistingApplicationEvents.BACK]: ExistingApplicationStates.CartSummary,
            },
            meta: {
              navigationSection: NavigationSections.PAYMENT_DETAILS,
              canGoBackViaHeader: true,
            },
          },
          [ExistingApplicationStates.CartReviewLoading]: {
            initial: 'loading',
            states: {
              loading: {
                tags: 'callingUpdateOrderItems',
                invoke: {
                  src: updateOrderItems,
                  onDone: [
                    // TODO: handle orderErrors
                    {
                      cond: (_, event) => !!event.data.orderErrors?.length,
                      actions: assign({
                        updateOrderErrors: (_, event) => event.data?.updateOrderErrors,
                        cartValidationErrors: (_, event) => event.data?.cartValidationErrors,
                        cartValidationItemErrors: (_, event) => event.data?.cartValidationItemErrors,
                        cartValidationItemWarnings: (_, event) => event.data?.cartValidationItemWarnings,
                      }),
                      target: '#ExistingApplication.VirtualCardApplication.CartSummary',
                    },
                    // item level errors
                    {
                      cond: (_, event) => !!event.data.cartValidationItemErrors && Object.keys(event.data.cartValidationItemErrors).length > 0,
                      // || Object.keys(event.data.cartValidationItemWarnings).length > 0, // warnings do not block yet
                      actions: [
                        assign({
                          cartValidationErrors: (_, event) => event.data?.cartValidationErrors,
                          cartValidationItemErrors: (_, event) => event.data?.cartValidationItemErrors,
                          cartValidationItemWarnings: (_, event) => event.data?.cartValidationItemWarnings,
                        }),
                        track('Cart validation item error'),
                      ],
                      target: '#ExistingApplication.VirtualCardApplication.CartSummary',
                    },
                    // cart level errors
                    {
                      cond: (_, event) => !!event.data.cartValidationErrors?.length,
                      actions: [
                        assign({
                          cartValidationErrors: (_, event) => event.data?.cartValidationErrors,
                          cartValidationItemErrors: (_, event) => event.data?.cartValidationItemErrors,
                          cartValidationItemWarnings: (_, event) => event.data?.cartValidationItemWarnings,
                        }),
                        track('Cart validation cart error'),
                      ],
                      target: '#ExistingApplication.VirtualCardApplication.CartError',
                    },
                    {
                      target: 'callingQueryOrder', // calling this here to use the pattern of moving the loader out of the contract summary
                      // only assign items here because this mutation only returns the details
                      actions: assign({ orderDetails: (context, event) => ({ ...context.orderDetails, items: event.data?.order?.details?.items }) }),
                    },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
              callingQueryOrder: MakeStateCallingQueryOrderWithCartValidation('#ExistingApplication.VirtualCardApplication.ContractSummaryEcomm'),
            },
          },
          [ExistingApplicationStates.CartError]: {
            on: {
              [ExistingApplicationEvents.EDIT_CART]: {
                actions: assign({ cartValidationItemWarnings: {}, updateOrderErrors: [] }),
                target: '#ExistingApplication.VirtualCardApplication.CartSummary',
              },
            },
          },
          [ExistingApplicationStates.ContractSummaryEcomm]: {
            // no longer requires requerying order because all codepaths implicitly requery the order
            initial: 'waiting',
            states: {
              // Handles resuming virtual card apps that are "DocsReady". Before a user is allowed to go back and edit their
              // cart, we have to know what items were in it. If there are no items in local state, we fetch the order from
              // AES to get the items from there. The user will then be able to click the button to go back and edit their cart
              // if desired.
              waiting: {
                on: {
                  [ExistingApplicationEvents.NEXT]: {
                    target: 'callingSelectLeaseOption',
                    actions: assign({ selectLeaseOptionErrors: [] }),
                  },
                },
              },
              callingSelectLeaseOption: {
                tags: 'calling',
                invoke: {
                  src: selectLeaseOption,
                  onDone: [
                    {
                      cond: (_, event) => event.data.selectLeaseOptionErrors?.length,
                      target: '#ExistingApplication.InternalError',
                    },
                    { target: '#ExistingApplication.VirtualCardApplication.LeaseAgreement' },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
            },
            on: {
              [ExistingApplicationEvents.BACK]: [
                {
                  // refresh handler to update the cart with the order items
                  cond: (context) => !context?.cart?.length,
                  actions: assign({
                    cart: (context) => getCartFromOrderDetails(context.orderDetails),
                  }),
                  target: ExistingApplicationStates.CartSummary,
                },
                { target: ExistingApplicationStates.CartSummary },
              ],
              [ExistingApplicationEvents.EDIT_DEBIT_INFO]: {
                target: ExistingApplicationStates.DebitCardEntry,
              },
            },
            meta: {
              navigationSection: NavigationSections.REVIEW_AND_PAY,
            },
          },
          [ExistingApplicationStates.ContractSummaryBM]: {
            initial: 'callingQueryOrder',
            entry: assign({ showNotAcknowledgedModal: false }),
            states: {
              // Just a precaution to ensure contract summaries exist
              callingQueryOrder: MakeStateCallingQueryOrder('callingGetAppItems'),
              callingGetAppItems: {
                tags: 'calling',
                invoke: {
                  src: getApplicationItems,
                  onDone: {
                    target: 'waiting',
                    actions: assign({
                      applicationItems: (_, event) => {
                        return event.data || [];
                      },
                    }),
                  },
                  onError: { actions: PropagateNetworkErrors },
                },
              },
              waiting: {
                on: {
                  [ExistingApplicationEvents.NEXT]: [
                    {
                      target: 'callingAcknowledgeLeaseOptions',
                      actions: assign({ selectLeaseOptionErrors: [], acknowledgeLeaseTermsErrors: [], customerSelectedLeaseTerm: (_, event) => event.data }),
                    },
                  ],
                  [ExistingApplicationEvents.REFRESH_CONTRACT_SUMMARY_STATUS]: {
                    target: 'refreshingOrderStatus',
                    actions: assign({ showNotAcknowledgedModal: false }),
                  },
                  [ExistingApplicationEvents.CLOSE_NOT_ACKNOWLEDGED_MODAL]: {
                    actions: assign({ showNotAcknowledgedModal: false }),
                  },
                },
              },
              callingAcknowledgeLeaseOptions: {
                tags: 'calling',
                invoke: {
                  src: acknowledgeLeaseOptions,
                  onDone: [
                    {
                      cond: (_, event) => event.data.selectLeaseOptionErrors?.length,
                      target: '#ExistingApplication.InternalError',
                    },
                    {
                      cond: (_, event) => event.data.acknowledgeLeaseTermsErrors?.length,
                      target: 'waiting',
                      actions: [
                        assign({
                          acknowledgeLeaseTermsErrors: (_context, event) => event.data.acknowledgeLeaseTermsErrors ?? [],
                        }),
                        trackContractSummaryAcknowledgeFailed,
                      ],
                    },
                    { target: 'callingSelectLeaseOption', actions: track('Contract Summary Acknowledge Successful') },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
              refreshingOrderStatus: {
                tags: 'calling',
                invoke: {
                  src: queryOrder,
                  onDone: {
                    target: 'checkRefreshResult',
                    actions: assign({
                      application: (_, event) => event.data?.application,
                      orderDetails: (_, event) => event.data?.details,
                    }),
                  },
                  onError: {
                    target: 'waiting',
                    actions: PropagateNetworkErrors,
                  },
                },
              },
              checkRefreshResult: {
                always: [
                  {
                    target: '#ExistingApplication.VirtualCardApplication.LeaseAgreement',
                    cond: (context) => context.application?.lease?.status === 'docsReady',
                  },
                  {
                    target: 'waiting',
                    actions: assign({ showNotAcknowledgedModal: true }),
                  },
                ],
              },
              callingSelectLeaseOption: {
                tags: 'calling',
                invoke: {
                  src: selectLeaseOption,
                  onDone: [
                    {
                      cond: (_, event) => event.data.selectLeaseOptionErrors?.length,
                      target: '#ExistingApplication.InternalError',
                    },
                    { target: '#ExistingApplication.VirtualCardApplication.LeaseAgreement' },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
            },
            on: {
              [ExistingApplicationEvents.EDIT_DEBIT_INFO]: {
                target: ExistingApplicationStates.DebitCardEntry,
                actions: assign({ fromContractSummary: true }),
              },
            },
            meta: {
              navigationSection: NavigationSections.REVIEW_AND_PAY,
            },
          },
          [ExistingApplicationStates.LeaseAgreement]: {
            initial: 'callingGenerateContract',
            entry: AssignInitialLeaseDocumentLanguage,
            states: {
              callingGenerateContract: {
                tags: 'callingGenerateContract',
                invoke: {
                  src: (context, _) => generateContract(context.orderId),
                  onDone: [
                    {
                      cond: (_, event) => event.data?.generateContractErrors?.length,
                      target: 'waiting', // let the page handle it
                      actions: [
                        assign({ generateContractErrors: (_, event: any) => event.data?.generateContractErrors }),
                        track('Internal Error Occurred - Lease Agreement'),
                      ],
                    },
                    {
                      actions: AssignGeneratedLeaseDocuments,
                      target: 'waiting',
                    },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
                on: {
                  [ExistingApplicationEvents.LANGUAGE_CHANGED]: createLanguageChangedTransitions(),
                },
              },
              waiting: {
                always: createLeaseDocumentLanguageReconciliation(),
                on: {
                  [ExistingApplicationEvents.NEXT]: 'callingGetIp',
                  [ExistingApplicationEvents.REFRESH_SIGNING_STATUS]: 'refreshingSigningStatus',
                  [ExistingApplicationEvents.LANGUAGE_CHANGED]: createLanguageChangedTransitions(),
                },
              },
              callingGetIp: {
                tags: 'calling',
                invoke: {
                  src: getIpAddress,
                  onDone: [{ target: 'callingSignLeaseAgreement', actions: assign({ ipAddress: (_, event) => event.data.ipAddress }) }],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
              callingSignLeaseAgreement: {
                tags: 'calling',
                invoke: {
                  src: (context) => signLeaseAgreement(context.application?.lease?.id, context.ipAddress),
                  onDone: [
                    {
                      cond: (_, event) => event.data?.signingErrors?.length,
                      target: 'waiting',
                      actions: [assign({ signingErrors: (_, event: any) => event.data?.signingErrors }), track('Signing Error')],
                    },
                    {
                      target: 'routeAfterSigning',
                      // OR-2774: tracked here rather than on the LeaseSigned page, because
                      // merchant and Protection Plus routes never render it (see routeAfterSigning).
                      actions: [assign({ leaseSigned: true }), track('Lease Agreement Sign Successful')],
                    },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
              ...createSigningRefreshStates(),
              routeAfterSigning: {
                always: [
                  {
                    target: '#ExistingApplication.VirtualCardApplication.VirtualCardPage',
                  },
                ],
              },
              // this state is only reachable via the contractSummaryPage transition
              callGetExistingLeaseDocuments: {
                tags: 'calling',
                invoke: {
                  src: (context, _) => getExistingLeaseDocuments(context.orderId),
                  onDone: [
                    {
                      cond: (_, event) => event.data?.getExistingLeaseDocumentsErrors?.length,
                      target: 'waiting', // let the page handle it
                      actions: [
                        assign({ getExistingLeaseDocumentsErrors: (_, event: any) => event.data?.getExistingLeaseDocumentsErrors }),
                        track('Internal Error Occurred - Getting Lease Agreement'),
                      ],
                    },
                    {
                      actions: AssignExistingLeaseDocuments,
                      target: 'waiting',
                    },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
            },
            on: {
              [ExistingApplicationEvents.BACK]: {
                target: '#ExistingApplication.VirtualCardApplication.ContractSummaryEcomm',
              },
              [ExistingApplicationEvents.LANGUAGE_CHANGED]: {
                cond: (_context, event) => !!event.data?.language,
                actions: AssignRequestedLeaseDocumentLanguage,
              },
            },
            meta: { navigationSection: NavigationSections.REVIEW_AND_PAY },
          },
          [ExistingApplicationStates.VirtualCardPage]: {
            initial: 'select',
            states: {
              // if there's no items, requery the order to get those items. This happens for refresh/view virtual card flow because there's no query order in that flow
              select: { always: [{ cond: (context) => !context.orderDetails?.items?.length, target: 'callingQueryOrder' }, { target: 'callingGetEmbedUrl' }] },
              callingQueryOrder: MakeStateCallingQueryOrder('callingGetEmbedUrl'),
              callingGetEmbedUrl: {
                tags: 'calling',
                invoke: {
                  src: (context: ExistingApplicationMachineContext) => queryEmbedUrl(context.orderId),
                  onDone: [
                    {
                      target: 'waiting',
                      actions: assign((_context: any, event: { data: GetEmbedUrlResult }) => ({
                        cardExpiration: event.data.cardExpiration,
                        embedUrl: event.data.embedUrl,
                        embedUrlError: event.data.embedUrlError,
                      })),
                    },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
              waiting: {
                on: {
                  [ExistingApplicationEvents.BACK]: {
                    target: '#ExistingApplication.VirtualCardApplication.CartSummary',
                    actions: assign({
                      embedUrl: '',
                      embedUrlError: null,
                      leaseSigned: false,
                    }),
                  },
                  [ExistingApplicationEvents.RETRY]: {
                    target: 'callingGetEmbedUrl',
                    actions: assign({
                      embedUrlError: null,
                    }),
                  },
                  [ExistingApplicationEvents.REMOVE_VIRTUAL_CARD]: {
                    target: 'callingRemoveVirtualCard',
                  },
                },
              },
              callingRemoveVirtualCard: {
                tags: 'calling',
                invoke: {
                  src: (context: ExistingApplicationMachineContext) => removeVirtualCard(getApplicationId(context.application)),
                  onDone: [
                    {
                      target: 'waiting',
                      cond: (_context, event) => !!event?.data?.removeVirtualCardErrors?.length,
                      actions: assign({
                        removeVirtualCardErrors: (_context, event) => event.data.removeVirtualCardErrors,
                      }),
                    },
                    {
                      target: '#ExistingApplication.VirtualCardApplication.CartSummary',
                      cond: (context) => !context?.cart?.length,
                      actions: [
                        assign({
                          embedUrl: '',
                          embedUrlError: null,
                          leaseSigned: false,
                          cart: (context) => getCartFromOrderDetails(context.orderDetails),
                        }),
                        () => setAppStatusParams('approved'),
                      ],
                    },
                    {
                      target: '#ExistingApplication.VirtualCardApplication.CartSummary',
                      actions: [
                        assign({
                          embedUrl: '',
                          embedUrlError: null,
                          leaseSigned: false,
                        }),
                        () => setAppStatusParams('approved'),
                      ],
                    },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
            },
          },
        },
      },
      [ExistingApplicationStates.FinalApproval]: {},
      [ExistingApplicationStates.CartValidation]: {
        meta: {
          navigationSection: NavigationSections.HIDE_PROGRESS_BAR,
        },
      },
      [ExistingApplicationStates.DebitCardHardDecline]: {},
      [ExistingApplicationStates.DebitCardSoftDecline]: {
        on: {
          [ExistingApplicationEvents.NEXT]: [
            {
              target: '#ExistingApplication.VirtualCardApplication.DebitCardEntry',
              cond: 'VirtualCardDealer',
            },
            { target: '#ExistingApplication.LeaseApplication.DebitCardEntry' },
          ],
        },
      }, // needs to go back to IP
      [ExistingApplicationStates.DebitCardFatalError]: {},
      [ExistingApplicationStates.BankAccountHardDecline]: {},
      [ExistingApplicationStates.ApprovalConfirmation]: {},
      [ExistingApplicationStates.InternalError]: {},
      // The exit to post-decision-machine (see exitToPostDecision). Final, like the long form
      // machine's: the ecomm machine leaves this state on the event, so nothing runs after it.
      [ExistingApplicationStates.PostDecision]: {
        type: 'final',
        entry: sendParent((context) => ({ type: EcommMachineEvents.POST_DECISION, data: { ...context } })),
      },
    },
    schema: {
      context: {} as ExistingApplicationMachineContext,
      events: {} as ExistingApplicationMachineEvents,
      guards: {} as ExistingApplicationMachineGuards,
    },
    predictableActionArguments: true,
    preserveActionOrder: true,
  },
  {
    actions: {
      log: (context, event, meta) => {
        console.log('Existing App Machine');
        console.log('current state:', meta.state?.value);
        console.log('context', context);
        console.log('event', event);
      },
    },
    guards: {
      VirtualCardDealer: (context: ExistingApplicationMachineContext) => !!context.dealer?.isVirtualCard,
      // Changing this to always performBAV if the flag
      PerformBav: (context, event) =>
        context.application?.lease?.draftStep === 'bankingInformation' || // normal value
        context.application?.lease?.draftStep === 'bankAccount', // value if in errors found
      DebitCardPage: (context, event) =>
        context.application?.lease?.draftStep === 'debitCardInformation' || // normal value
        context.application?.lease?.draftStep === 'debitCardError', // value if in errors found
      PaymentError: (context: ExistingApplicationMachineContext) => {
        const declineReason = context.application?.lease?.declineReason;
        return ['debitCardError'].includes(declineReason!);
      },
      LeaseSkipToPrequalification: (context: ExistingApplicationMachineContext) => context.skipToPage === 'Prequalification',
      LeaseSkipToDebitCardEntry: (context: ExistingApplicationMachineContext) => context.skipToPage === 'DebitCardEntry',
      LeaseSkipToBankDetails: (context: ExistingApplicationMachineContext) => context.skipToPage === 'BankDetails', // likely unused, but we need to investigate before deleting
      LeasePrequalified: (context: ExistingApplicationMachineContext) => context.application?.lease?.status === 'preApproved',
      LeaseDocsSigned: (context: ExistingApplicationMachineContext) => context.application?.lease?.status === 'docsSigned',
      LeaseReadyForFunding: (context: ExistingApplicationMachineContext) => context.application?.lease?.status === 'readyForFunding',
      LeasePaymentError: (context: ExistingApplicationMachineContext) => {
        const declineReason = context.application?.lease?.declineReason;
        return ['creditCardNumber', 'creditCardExpiration', 'debitCardError'].includes(declineReason!);
      },
      LeaseBankAccountError: (context: ExistingApplicationMachineContext) => {
        const declineReason = context.application?.lease?.declineReason;
        return declineReason === 'bankAccount';
      },
      IsBrickAndMortar: (context, event) => {
        return context.dealer?.channelType === ChannelTypes.BrickAndMortar;
      },
      IsCardExpired: (context: ExistingApplicationMachineContext) => !!context.application?.lease?.card?.isExpired,
      isLeaseOTB: (context: ExistingApplicationMachineContext) => !!context.application?.lease?.isOtb && !context.application?.lease?.card?.isExpired,
      IsEcommApprovalOnly: (context: ExistingApplicationMachineContext) => getEcommApprovalOnly(context.dealer),
      LoanDecline: (context: ExistingApplicationMachineContext) => {
        return context.application?.loan?.status === 'declined';
      },
      LeaseDecline: (context: ExistingApplicationMachineContext) => {
        return context.application?.lease?.status === 'declined';
      },
      OrderHasItems: (context: ExistingApplicationMachineContext, event: any) => !!event?.data?.order?.details?.items?.length,
      HasItems: (context: ExistingApplicationMachineContext) => context.cart.length > 0,
      DebitCardHardDecline: (context: ExistingApplicationMachineContext, event: any) =>
        context.updateDebitInfoErrors &&
        context.updateDebitInfoErrors.length > 0 &&
        context.updateDebitInfoErrors.some((err: UpdateDebitErrors) => err.__typename === 'HardDecline'),
      DebitCardSoftDecline: (context: ExistingApplicationMachineContext, event: any) =>
        context.updateDebitInfoErrors &&
        context.updateDebitInfoErrors.length > 0 &&
        context.updateDebitInfoErrors.some((err: UpdateDebitErrors) => err.__typename === 'SoftDecline'),
      DebitCardSoftDeclineAndErrorDetailsEnabled: (context: ExistingApplicationMachineContext, _) =>
        context.updateDebitInfoErrors &&
        context.updateDebitInfoErrors.length > 0 &&
        context.updateDebitInfoErrors.some((err: UpdateDebitErrors) => err.__typename === 'SoftDecline') &&
        context.ldClient.variation(FLAG_SHOW_DEBIT_DECISION_SHORT_FORM, false),
      DebitCardFatalError: (context: ExistingApplicationMachineContext, event: any) =>
        context.updateDebitInfoErrors &&
        context.updateDebitInfoErrors.length > 0 &&
        context.updateDebitInfoErrors.some((err: UpdateDebitErrors) => err.__typename !== 'PrepaidCardError'),
      DebitCardPrepaidError: (context: ExistingApplicationMachineContext, event: any) =>
        context.updateDebitInfoErrors &&
        context.updateDebitInfoErrors.length > 0 &&
        context.updateDebitInfoErrors.some(
          (err: UpdateDebitErrors) =>
            err.__typename === 'SoftDecline' &&
            (err.extensions?.declineType === SoftDeclineTypes.PrepaidCardsNotAllowed || err.extensions?.declineType === SoftDeclineTypes.CreditCardsNotAllowed),
        ),
      PaymentSelectFF: (context, event) => {
        return determinePlaidFF(context);
      },
      EnterPaymentSelectFF: (context, event) => {
        return (
          determinePlaidFF(context) &&
          ((context?.dealer.formType === FormTypes.Short && context?.application?.lease?.appStep === AppSteps.BankingInformation) ||
            ((context?.dealer.formType === FormTypes.Long || context?.dealer.formType === FormTypes.LongV2) &&
              context?.application?.lease?.draftStep === 'bankAccount'))
        );
      },
      TokenizedAcct: (_context, event) =>
        event.data.updateBankAccountErrors.some(
          (err: UpdateBankAccountErrors) => err.__typename === 'BankConnectionError' && err?.extensions?.errCode === BankConnectionErrTypes.Tokenized,
        ),
      InternetBankError: (_, event) =>
        event.data?.updateBankAccountErrors?.some(
          (err: UpdateBankAccountErrors) => err.__typename === 'SoftDecline' && err?.extensions?.declineType === SoftDeclineTypes.InternetBankError,
        ),
      BankError: (_, event) =>
        event.data?.updateBankAccountErrors?.some(
          (err: UpdateBankAccountErrors) => err.__typename === 'SoftDecline' && err?.extensions?.declineType === SoftDeclineTypes.BankError,
        ),
      BankAccountHardDecline: (context, event) => event.data?.updateBankAccountErrors?.some((err: UpdateBankAccountErrors) => err.__typename === 'HardDecline'),
      IneligibleBank: (_context, event) => {
        // General error coming back, etc...
        return event.data?.updateBankAccountErrors?.length;
      },
      PlaidSuccess: (_context, event) => {
        return event.data.updateBankAccountErrors && event.data.updateBankAccountErrors.length === 0;
      },
      LinkTokenFailure: (_context, event) => {
        return event.data.generateBankConnectorTokenErrors.length > 0;
      },
      HasUpdateEmploymentErrors: (_context, event) => {
        return event.data?.updateEmploymentErrors?.length;
      },
      AppStepPayFrequency: (context) => {
        return context?.application?.lease?.appStep === AppSteps.IncomeSource;
      },
      AppStepBankingInformation: (context) => {
        return context?.application?.lease?.appStep === AppSteps.BankingInformation;
      },
      AppStepDebitInformation: (context) => {
        return context?.application?.lease?.appStep === AppSteps.DebitCardInformation;
      },
      OfferProtectionPlus: (context) =>
        context.highestSupportedApplicationMilestone >= 4 &&
        context.dealer.channelType === ChannelTypes.BrickAndMortar &&
        !!context.dealer.isLTOInsurance &&
        !['UT', 'OK', 'IN', 'AK', 'RI'].includes(context.dealer.dealerAddress.state) &&
        context.application?.customer?.protectionPlusStatus != ProtectionPlusStatus.Enrolled &&
        context.application?.customer?.protectionPlusStatus != ProtectionPlusStatus.OptedIn,
      IsDocsSigned: (context) => context.application?.lease?.status === 'docsSigned',
    },
  },
);
